/* GEOTECH · Collection list (ARC) · Velocity dropdown + protected purchasing */
(() => {
  if (customElements.get('geotech-collection-list')) return;

  class GeotechCollectionList extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.busyForms = new WeakSet();
      this.pendingControllers = new Set();
      this.returnFocus = null;
      this.notifyReturnFocus = null;
      this.openDropdown = null;
      this.dropTimer = null;
      this.typeahead = { value: '', timer: null };
      this.pageScroll = { x: 0, y: 0 };

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDialogCancel = this.onDialogCancel.bind(this);
      this.onNotifyCancel = this.onNotifyCancel.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onModalScroll = this.onModalScroll.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.list = this.querySelector('[data-gcl-list]');
      this.items = Array.from(this.querySelectorAll('[data-gcl-item]'));
      this.sortSelect = this.querySelector('[data-gcl-sort]');
      this.countEls = Array.from(this.querySelectorAll('[data-gcl-count]'));
      this.modal = this.querySelector('[data-gcl-modal]');
      this.modalContent = this.querySelector('[data-gcl-modal-content]');
      this.modalClose = this.querySelector('[data-gcl-modal-close]');
      this.notifyModal = this.querySelector('[data-gcl-notify]');
      this.notifyClose = this.querySelector('[data-gcl-notify-close]');
      this.notifyTitle = this.querySelector('[data-gcl-notify-title]');
      this.notifyProduct = this.querySelector('[data-gcl-notify-product]');
      this.notifyCopy = this.querySelector('[data-gcl-notify-copy]');
      this.notifyTags = this.querySelector('[data-gcl-notify-tags]');

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      this.modal?.addEventListener('cancel', this.onDialogCancel);
      this.notifyModal?.addEventListener('cancel', this.onNotifyCancel);
      this.modalContent?.addEventListener('scroll', this.onModalScroll, true);
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      if (this.sortSelect) {
        this.fitSelect();
        this.applySort();
      }

      if (this.querySelector('[data-gcl-notify-result]')) {
        requestAnimationFrame(() => this.openSavedNotifyResult());
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('keydown', this.onKeydown);
      this.modal?.removeEventListener('cancel', this.onDialogCancel);
      this.notifyModal?.removeEventListener('cancel', this.onNotifyCancel);
      this.modalContent?.removeEventListener('scroll', this.onModalScroll, true);
      window.removeEventListener('resize', this.onViewportChange);

      this.stopDropTimer();
      window.clearTimeout(this.typeahead.timer);
      this.pendingControllers.forEach((controller) => controller.abort());
      this.pendingControllers.clear();
      this.initialized = false;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-gcl-modal-open]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.openQuickShop(trigger);
        return;
      }

      const modalClose = target.closest('[data-gcl-modal-close]');
      if (modalClose && this.contains(modalClose)) {
        event.preventDefault();
        this.closeQuickShop();
        return;
      }

      const notifyClose = target.closest('[data-gcl-notify-close]');
      if (notifyClose && this.contains(notifyClose)) {
        event.preventDefault();
        this.closeNotify();
        return;
      }

      const notifyTrigger = target.closest('[data-gcl-notify-trigger]');
      if (notifyTrigger && this.contains(notifyTrigger)) {
        event.preventDefault();
        event.stopPropagation();
        this.openNotify(notifyTrigger);
        return;
      }

      const pickerTrigger = target.closest('[data-gcl-variant-trigger]');
      if (pickerTrigger && this.contains(pickerTrigger)) {
        event.preventDefault();
        const dropdown = pickerTrigger.closest('[data-gcl-variant-dropdown]');
        if (dropdown === this.openDropdown) this.closeVariantDropdown(dropdown, true);
        else this.openVariantDropdown(dropdown, 'selected');
        return;
      }

      const option = target.closest('[data-gcl-variant-option]');
      if (option && this.contains(option)) {
        event.preventDefault();
        this.selectVariant(option);
        return;
      }

      if (this.openDropdown && !target.closest('[data-gcl-variant-dropdown]')) {
        this.closeVariantDropdown(this.openDropdown, false);
      }

      if (this.notifyModal && event.target === this.notifyModal) {
        this.closeNotify();
        return;
      }

      if (this.modal && event.target === this.modal) {
        this.closeQuickShop();
      }
    }

    onChange(event) {
      if (event.target.matches('[data-gcl-sort]')) {
        this.fitSelect();
        this.applySort();
      }
    }

    onKeydown(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-gcl-variant-trigger]');
      if (trigger) {
        const dropdown = trigger.closest('[data-gcl-variant-dropdown]');
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          this.openVariantDropdown(dropdown, event.key === 'ArrowUp' ? 'last' : 'selected');
        } else if (event.key === 'Escape' && dropdown === this.openDropdown) {
          event.preventDefault();
          this.closeVariantDropdown(dropdown, true);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          this.openVariantDropdown(dropdown, 'selected');
          this.focusTypeahead(dropdown, event.key);
        }
        return;
      }

      const option = target.closest('[data-gcl-variant-option]');
      if (!option) return;

      const dropdown = option.closest('[data-gcl-variant-dropdown]');
      const options = this.variantOptions(dropdown);
      const index = options.indexOf(option);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        options[(index + step + options.length) % options.length]?.focus({ preventScroll: true });
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        options[event.key === 'Home' ? 0 : options.length - 1]?.focus({ preventScroll: true });
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectVariant(option);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeVariantDropdown(dropdown, true);
      } else if (event.key === 'Tab') {
        this.closeVariantDropdown(dropdown, false);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.focusTypeahead(dropdown, event.key);
      }
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.closeQuickShop();
    }

    onNotifyCancel(event) {
      event.preventDefault();
      this.closeNotify();
    }

    onViewportChange() {
      if (this.openDropdown) this.closeVariantDropdown(this.openDropdown, false);
    }

    onModalScroll(event) {
      if (this.openDropdown && event.target instanceof Element && this.openDropdown.contains(event.target)) return;
      if (this.openDropdown) this.closeVariantDropdown(this.openDropdown, false);
    }

    fitSelect() {
      const select = this.sortSelect;
      if (!select) return;
      const option = select.options[select.selectedIndex];
      if (!option) return;

      const probe = document.createElement('span');
      const style = getComputedStyle(select);
      probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${style.font};letter-spacing:${style.letterSpacing}`;
      probe.textContent = option.textContent || '';
      document.body.appendChild(probe);

      const width = probe.getBoundingClientRect().width +
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0) + 4;
      probe.remove();
      select.style.width = `${Math.ceil(width)}px`;
    }

    compare(a, b, mode) {
      const index = (el) => Number(el.dataset.index) || 0;
      const products = (el) => Number(el.dataset.products) || 0;
      const published = (el) => Number(el.dataset.published) || 0;
      const title = (el) => (el.dataset.title || '').trim();
      let result = 0;

      switch (mode) {
        case 'alphabetical':
          result = title(a).localeCompare(title(b), undefined, { sensitivity: 'base' });
          break;
        case 'alphabetical-reversed':
          result = title(b).localeCompare(title(a), undefined, { sensitivity: 'base' });
          break;
        case 'products-high':
          result = products(b) - products(a);
          break;
        case 'products-low':
          result = products(a) - products(b);
          break;
        case 'newest':
          result = published(b) - published(a);
          break;
        case 'oldest':
          result = published(a) - published(b);
          break;
        default:
          result = 0;
      }

      return result || index(a) - index(b);
    }

    applySort() {
      if (!this.list) return;
      const mode = this.sortSelect ? this.sortSelect.value : 'featured';
      const sorted = [...this.items].sort((a, b) => this.compare(a, b, mode));
      const fragment = document.createDocumentFragment();
      sorted.forEach((item) => fragment.appendChild(item));
      this.list.appendChild(fragment);
      this.countEls.forEach((el) => { el.textContent = String(this.items.length); });
    }

    restorePageScroll() {
      const { x, y } = this.pageScroll || { x: 0, y: 0 };
      if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
    }

    openQuickShop(trigger) {
      if (!this.modal || !this.modalContent) return;
      const templateId = trigger.dataset.gclTemplate;
      const template = templateId && window.CSS?.escape
        ? this.querySelector(`#${CSS.escape(templateId)}`)
        : null;
      if (!(template instanceof HTMLTemplateElement)) return;

      this.pageScroll = { x: window.scrollX, y: window.scrollY };
      this.returnFocus = trigger;
      this.modalContent.replaceChildren(template.content.cloneNode(true));

      const productScroller = this.modalContent.querySelector('[data-gcl-quick-products]');
      if (productScroller) productScroller.scrollTop = 0;

      const title = this.modalContent.querySelector('.gcl-quick__title');
      this.modal.setAttribute('aria-label', title?.textContent?.trim() || 'Collection quick shop');
      this.updateAllPurchaseStates();
      this.startDropTimer();

      if (typeof this.modal.showModal === 'function') {
        if (!this.modal.open) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }

      this.restorePageScroll();
      requestAnimationFrame(() => {
        this.modalClose?.focus({ preventScroll: true });
        this.restorePageScroll();
      });
    }

    closeQuickShop() {
      if (!this.modal || !this.modalContent) return;
      if (this.notifyModal?.open) this.closeNotify(false);
      this.closeVariantDropdown(this.openDropdown, false);
      this.stopDropTimer();

      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else this.modal.removeAttribute('open');

      this.modalContent.replaceChildren();
      this.restorePageScroll();
      const focusTarget = this.returnFocus;
      this.returnFocus = null;

      requestAnimationFrame(() => {
        if (focusTarget && document.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
        this.restorePageScroll();
      });
    }

    variantOptions(dropdown) {
      return dropdown ? Array.from(dropdown.querySelectorAll('[data-gcl-variant-option]')) : [];
    }

    openVariantDropdown(dropdown, focusMode = 'selected') {
      if (!dropdown) return;
      if (this.openDropdown && this.openDropdown !== dropdown) {
        this.closeVariantDropdown(this.openDropdown, false);
      }

      const trigger = dropdown.querySelector('[data-gcl-variant-trigger]');
      const menu = dropdown.querySelector('[data-gcl-variant-menu]');
      if (!trigger || !menu) return;

      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('is-open');
      dropdown.closest('.gcl-quick-product')?.classList.add('is-picker-open');
      this.openDropdown = dropdown;
      this.positionVariantMenu(dropdown);

      const options = this.variantOptions(dropdown);
      const selected = options.find((item) => item.getAttribute('aria-selected') === 'true');
      const focusTarget = focusMode === 'last' ? options.at(-1) : (selected || options[0]);
      requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
    }

    positionVariantMenu(dropdown) {
      const trigger = dropdown?.querySelector('[data-gcl-variant-trigger]');
      const menu = dropdown?.querySelector('[data-gcl-variant-menu]');
      if (!trigger || !menu) return;

      dropdown.classList.remove('opens-up');
      menu.style.removeProperty('max-height');

      const scroller = dropdown.closest('[data-gcl-quick-products]');
      const triggerRect = trigger.getBoundingClientRect();
      const scrollerRect = scroller?.getBoundingClientRect();
      const topBoundary = Math.max(8, scrollerRect?.top ?? 8);
      const bottomBoundary = Math.min(window.innerHeight - 8, scrollerRect?.bottom ?? window.innerHeight - 8);
      const above = Math.max(0, triggerRect.top - topBoundary - 8);
      const below = Math.max(0, bottomBoundary - triggerRect.bottom - 8);
      const desired = Math.min(menu.scrollHeight, 280);
      const opensUp = below < desired && above > below;
      const available = opensUp ? above : below;

      dropdown.classList.toggle('opens-up', opensUp);
      menu.style.maxHeight = `${Math.max(96, Math.min(280, available || desired))}px`;
    }

    closeVariantDropdown(dropdown, restoreFocus = false) {
      if (!dropdown) return;
      const trigger = dropdown.querySelector('[data-gcl-variant-trigger]');
      const menu = dropdown.querySelector('[data-gcl-variant-menu]');
      if (menu) {
        menu.hidden = true;
        menu.style.removeProperty('max-height');
      }
      trigger?.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('is-open', 'opens-up');
      dropdown.closest('.gcl-quick-product')?.classList.remove('is-picker-open');
      if (this.openDropdown === dropdown) this.openDropdown = null;
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }

    focusTypeahead(dropdown, character) {
      window.clearTimeout(this.typeahead.timer);
      this.typeahead.value += character.toLocaleLowerCase();
      const options = this.variantOptions(dropdown);
      const match = options.find((option) =>
        (option.dataset.label || option.textContent || '').trim().toLocaleLowerCase().startsWith(this.typeahead.value)
      );
      match?.focus({ preventScroll: true });
      this.typeahead.timer = window.setTimeout(() => { this.typeahead.value = ''; }, 700);
    }

    selectVariant(option) {
      const dropdown = option.closest('[data-gcl-variant-dropdown]');
      const form = option.closest('.gcl-quick-product__form');
      const input = form?.querySelector('[data-gcl-variant-id]');
      const trigger = dropdown?.querySelector('[data-gcl-variant-trigger]');
      const label = dropdown?.querySelector('[data-gcl-variant-label]');
      const status = form?.querySelector('[data-gcl-quick-status]');
      if (!dropdown || !form || !input) return;

      input.value = option.dataset.variantId || '';
      ['available', 'inventoryManaged', 'inventoryQuantity'].forEach((key) => {
        input.dataset[key] = option.dataset[key] || '';
      });

      this.variantOptions(dropdown).forEach((item) => {
        item.setAttribute('aria-selected', item === option ? 'true' : 'false');
      });

      if (label) label.textContent = option.dataset.label || option.textContent.trim();
      if (trigger) {
        const productTitle = form.closest('.gcl-quick-product')?.dataset.gclProductTitle || 'this product';
        trigger.setAttribute('aria-label', `Choose an option for ${productTitle}. Selected: ${option.dataset.label || ''}`);
      }

      this.updateVariantPrice(option);
      this.updatePurchaseState(form);
      if (status) {
        status.removeAttribute('data-error');
        status.textContent = '';
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.closeVariantDropdown(dropdown, true);
    }

    updateVariantPrice(source) {
      const card = source?.closest('.gcl-quick-product');
      if (!source || !card) return;
      const price = card.querySelector('[data-gcl-quick-price]');
      const compare = card.querySelector('[data-gcl-quick-compare]');
      const priceRaw = Number(source.dataset.priceRaw || 0);
      const compareRaw = Number(source.dataset.compareRaw || 0);

      if (price && source.dataset.priceText) price.textContent = source.dataset.priceText;
      if (compare) {
        const showCompare = compareRaw > priceRaw && Boolean(source.dataset.compareText);
        compare.hidden = !showCompare;
        if (showCompare) compare.textContent = source.dataset.compareText;
      }
    }

    variantStateFromDataset(dataset) {
      if (!dataset) return 'soldout';
      if (dataset.available === 'false') return 'soldout';
      if (dataset.inventoryManaged === 'true' && Number(dataset.inventoryQuantity) <= 0) return 'soldout';
      return 'available';
    }

    purchaseState(form) {
      if (!form) return 'soldout';
      const dropAt = Number(form.closest('.gcl-quick-product')?.dataset.gclDropAtMs || 0);
      if (Number.isFinite(dropAt) && dropAt > Date.now()) return 'predrop';
      return this.variantStateFromDataset(form.querySelector('[data-gcl-variant-id]')?.dataset);
    }

    updatePurchaseState(form) {
      if (!form) return 'soldout';
      const state = this.purchaseState(form);
      const add = form.querySelector('[data-gcl-add-to-bag]');
      const notify = form.querySelector('[data-gcl-notify-trigger]');
      const notifyLabel = notify?.querySelector('[data-gcl-notify-label]');
      const dropdown = form.querySelector('[data-gcl-variant-dropdown]');
      const selected = dropdown?.querySelector('[data-gcl-variant-option][aria-selected="true"]');
      const stateLabel = dropdown?.querySelector('[data-gcl-variant-state]');

      form.dataset.gclPurchaseState = state;
      if (add) {
        add.hidden = state !== 'available';
        if (state === 'available' && add.getAttribute('aria-busy') !== 'true') add.disabled = false;
      }
      if (notify) {
        notify.hidden = state === 'available';
        notify.dataset.gclNotifyReason = state === 'predrop' ? 'predrop' : 'restock';
      }
      if (notifyLabel) {
        notifyLabel.textContent = state === 'predrop' ? 'Notify me' : 'Notify me when it’s back';
      }
      if (stateLabel) {
        stateLabel.textContent = state === 'predrop' ? 'Drops soon' : (state === 'soldout' ? 'Sold out' : 'Available');
      }

      this.variantOptions(dropdown).forEach((option) => {
        const optionState = state === 'predrop' ? 'predrop' : this.variantStateFromDataset(option.dataset);
        const optionStateLabel = option.querySelector('.gcl-variant-picker__option-state');
        if (optionStateLabel) {
          optionStateLabel.textContent = optionState === 'predrop' ? 'Drops soon' : (optionState === 'soldout' ? 'Sold out' : 'Available');
        }
      });

      if (selected) this.updateVariantPrice(selected);
      return state;
    }

    updateAllPurchaseStates() {
      this.modalContent?.querySelectorAll('.gcl-quick-product__form').forEach((form) => this.updatePurchaseState(form));
    }

    startDropTimer() {
      this.stopDropTimer();
      const tick = () => {
        this.updateAllPurchaseStates();
        const hasFutureDrop = Array.from(this.modalContent?.querySelectorAll('.gcl-quick-product__form') || [])
          .some((form) => Number(form.closest('.gcl-quick-product')?.dataset.gclDropAtMs || 0) > Date.now());
        if (!hasFutureDrop) this.stopDropTimer();
      };
      tick();
      if (Array.from(this.modalContent?.querySelectorAll('.gcl-quick-product__form') || [])
        .some((form) => Number(form.closest('.gcl-quick-product')?.dataset.gclDropAtMs || 0) > Date.now())) {
        this.dropTimer = window.setInterval(tick, 1000);
      }
    }

    stopDropTimer() {
      if (this.dropTimer) window.clearInterval(this.dropTimer);
      this.dropTimer = null;
    }

    openNotify(trigger) {
      const form = trigger?.closest('.gcl-quick-product__form');
      const card = trigger?.closest('.gcl-quick-product');
      const state = this.updatePurchaseState(form);
      if (!this.notifyModal || state === 'available') return;

      const input = form?.querySelector('[data-gcl-variant-id]');
      const selectedOption = form?.querySelector('[data-gcl-variant-option][aria-selected="true"]');
      const productTitle = card?.dataset.gclProductTitle || 'Product';
      const productHandle = card?.dataset.gclProductHandle || 'product';
      const variantLabel = selectedOption?.dataset.label || '';
      const safe = (value) => String(value || '').replace(/[,\n\r]/g, ' ').replace(/\s+/g, ' ').trim();

      this.notifyReturnFocus = trigger;
      if (this.notifyTitle) {
        this.notifyTitle.textContent = state === 'predrop' ? 'DON’T MISS THE DROP' : 'BE FIRST WHEN IT’S BACK';
      }
      if (this.notifyProduct) {
        this.notifyProduct.textContent = [productTitle, variantLabel === 'Default Title' ? '' : variantLabel].filter(Boolean).join(' · ');
      }
      if (this.notifyCopy) {
        this.notifyCopy.textContent = state === 'predrop'
          ? 'Leave your email and we’ll notify you before this release goes live.'
          : 'Leave your email and we’ll notify you when this piece becomes available again.';
      }
      if (this.notifyTags) {
        this.notifyTags.value = [
          state === 'predrop' ? 'geotech-drop-alert' : 'geotech-restock-alert',
          `product:${safe(productHandle)}`,
          input?.value ? `variant:${safe(input.value)}` : ''
        ].filter(Boolean).join(',');
      }

      try {
        if (typeof this.notifyModal.showModal === 'function') {
          if (!this.notifyModal.open) this.notifyModal.showModal();
        } else {
          this.notifyModal.setAttribute('open', '');
        }
      } catch (_) {
        this.closeQuickShop();
        this.notifyModal.showModal?.();
      }

      requestAnimationFrame(() => {
        this.notifyModal.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      });
    }

    openSavedNotifyResult() {
      if (!this.notifyModal) return;
      if (typeof this.notifyModal.showModal === 'function') this.notifyModal.showModal();
      else this.notifyModal.setAttribute('open', '');
      requestAnimationFrame(() => this.notifyClose?.focus({ preventScroll: true }));
    }

    closeNotify(restoreFocus = true) {
      if (!this.notifyModal) return;
      if (typeof this.notifyModal.close === 'function' && this.notifyModal.open) this.notifyModal.close();
      else this.notifyModal.removeAttribute('open');
      const focusTarget = this.notifyReturnFocus;
      this.notifyReturnFocus = null;
      if (restoreFocus && focusTarget?.isConnected) {
        requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    }

    async refreshCartCount(root) {
      try {
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
          const accessibleCount = bubble.querySelector('.visually-hidden') || bubble.querySelector('span:not([aria-hidden="true"])');
          if (visibleCount) visibleCount.textContent = String(cart.item_count);
          if (accessibleCount) accessibleCount.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
          bubble.classList.toggle('hidden', cart.item_count <= 0);
        });
        document.dispatchEvent(new CustomEvent('geotech:cart-updated', { detail: { cart } }));
      } catch (_) {
        /* Cart count refresh is non-critical. */
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('.gcl-quick-product__form');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const state = this.updatePurchaseState(form);
      if (state !== 'available') {
        const notify = form.querySelector('[data-gcl-notify-trigger]');
        if (notify) this.openNotify(notify);
        return;
      }

      if (this.busyForms.has(form)) return;
      const button = form.querySelector('[data-gcl-add-to-bag]');
      const status = form.querySelector('[data-gcl-quick-status]');
      if (!button || button.disabled || button.hidden) return;

      this.busyForms.add(form);
      const originalHTML = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      button.textContent = 'Adding…';
      form.setAttribute('aria-busy', 'true');

      if (status) {
        status.removeAttribute('data-error');
        status.textContent = 'Adding to bag…';
      }

      const controller = new AbortController();
      this.pendingControllers.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = new FormData(form);
        const returnFocus = this.returnFocus;

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.append('sections', cart.getSectionsToRender().map((section) => section.id));
          body.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(returnFocus || button);
        }

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
          signal: controller.signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.description || payload?.message || 'Unable to add this item to your bag.');

        button.classList.remove('is-loading');
        button.textContent = 'Added ✓';
        if (status) status.textContent = 'Added to bag.';

        if (cart && typeof cart.renderContents === 'function') {
          this.closeQuickShop();
          cart.classList.remove('is-empty');
          cart.renderContents(payload);
        } else {
          await this.refreshCartCount(root);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'geotech-collection-list',
            productVariantId: body.get('id'),
            cartData: payload
          });
        }
        document.dispatchEvent(new CustomEvent('geotech:item-added', { detail: { item: payload } }));

        window.setTimeout(() => {
          this.busyForms.delete(form);
          if (!document.contains(button)) return;
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
          button.innerHTML = originalHTML;
          form.removeAttribute('aria-busy');
          this.updatePurchaseState(form);
        }, 1100);
      } catch (error) {
        this.busyForms.delete(form);
        if (!this.isConnected && error?.name === 'AbortError') return;

        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
        button.innerHTML = originalHTML;
        form.removeAttribute('aria-busy');
        this.updatePurchaseState(form);

        if (status) {
          status.setAttribute('data-error', 'true');
          status.textContent = error?.name === 'AbortError'
            ? 'Adding took too long. Please try again.'
            : (error?.message || 'Unable to add this item. Please try again.');
        }
      } finally {
        window.clearTimeout(timeout);
        this.pendingControllers.delete(controller);
      }
    }
  }

  customElements.define('geotech-collection-list', GeotechCollectionList);
})();